import {StylesService} from '../styles/styles.service';

declare let mxUtils: any;
import {Injectable, Renderer2, RendererFactory2} from '@angular/core';
import {EditorRegistryService} from '../mx-graph/editor/editor-registry.service';
import {isActivity, isImage, isSticker, isText} from './shapes-detectors.util';
import {MxCell} from '../../editor-interface/shared/models/mx-cell.interface';

@Injectable()
export class GraphHelperService {
  private r2: Renderer2;

  constructor(
    private registry: EditorRegistryService,
    private stylesSvc: StylesService,
    r2f: RendererFactory2
  ) {
    this.r2 = r2f.createRenderer(null, null);
  }

  public all() {
    return [
      ...this.allActivities(),
      ...this.allFlowlines(),
      ...this.allTexts(),
      ...this.allConnectorAndTerminators(),
      ...this.allStickers(),
      ...this.allImages()
    ];
  }

  public allActivities() {
    return this.getChildrenOf(2);
  }

  public allFlowlines() {
    return this.getChildrenOf(3);
  }

  public allTexts() {
    return this.getChildrenOf(4);
  }

  public allConnectorAndTerminators() {
    return this.getChildrenOf(9);
  }

  public allImages() {
    return this.getChildrenOf(5);
  }

  public allStickers() {
    return this.getChildrenOf(7);
  }

  public allSwimlanes() {
    return this.getChildrenOf(8);
  }

  public isCellLockable(cell) {
    return isActivity(cell) || isText(cell) || isImage(cell) || isSticker(cell);
  }

  public connectorAndTerminatorParent() {
    const graph = this.registry.getGraph();

    return graph.model.getCell(9);
  }

  public activityParent() {
    const graph = this.registry.getGraph();

    return graph.model.getCell(2);
  }

  public flowlineParent() {
    const graph = this.registry.getGraph();

    return graph.model.getCell(3);
  }

  public textParent() {
    const graph = this.registry.getGraph();

    return graph.model.getCell(4);
  }

  public imageParent() {
    const graph = this.registry.getGraph();

    return graph.model.getCell(5);
  }

  public stickerParent() {
    const graph = this.registry.getGraph();

    return graph.model.getCell(7);
  }

  public swimlaneParent() {
    const graph = this.registry.getGraph();

    return graph.model.getCell(8);
  }

  private getChildrenOf(layer) {
    const graph = this.registry.getGraph();
    const model = graph.getModel();
    const container = graph.model.getCell(layer);

    return model.getChildren(container) || [];
  }

  public isCellEditing() {
    const cellEditor = this.registry.getGraph().cellEditor;

    return Boolean(cellEditor.editingCell);
  }

  public getCellMetadata(cell, name) {
    if (cell.comp) {
      // make sure component member is public
      return cell.comp.instance[name];
    } else {
      const tempElem = this.r2.createElement('div');

      if (cell.value && cell.value.length > 0) {
        tempElem.innerHTML = cell.value;
      } else if (cell.editingValue && cell.editingValue.length > 0) {
        tempElem.innerHTML = cell.editingValue;
      } else {
        tempElem.innerHTML = '';
      }

      const metaCarriers = tempElem.getElementsByTagName('input');

      for (const item of metaCarriers) {
        if (String(item.name) === String(name)) {
          return item.value;
        }
      }
    }
  }

  public redrawInteractions(interactions = []) {
    // from diagramjson
    const graph = this.registry.getGraph();

    interactions.forEach((ia, i) => {
      const cell = graph.model.getCell(`${ia.flowlineMxKey}-ian`);

      if (cell) {
        graph.model.setValue(cell, i + 1);
      }
    });
  }

  public getFreeNumber(parentLayer: number, bookedNumbers?: Array<number>) {
    const layerChildren = this.getChildrenOf(parentLayer);

    const map = layerChildren.reduce((accum, child) => {
      const number = Number(this.getCellMetadata(child, 'number'));

      if (number && !Number.isNaN(number)) {
        accum[number] = true;
      }

      return accum;
    }, {});

    if (bookedNumbers) {
      bookedNumbers.forEach(num => {
        map[num] = true;
      });
    }

    let i = 0;
    let result = void 0;

    while (!result) {
      if (!map[++i]) {
        result = i;
      }
    }

    return result;
  }

  public mxStyleApply(mxStyles, ourStyles, mxKey, isCustom, style, type) {
    const model = this.registry.getGraph().model;
    const cell = model.getCell(mxKey);
    let styleString = cell.style;
    const length = ourStyles.length;
    const styleSource = isCustom ? style : this.stylesSvc.getPrimary(type);

    for (let i = 0; i < length; i++) {
      styleString = mxUtils.setStyle(styleString, mxStyles[i], styleSource[ourStyles[i]]);
    }

    styleString = mxUtils.setStyle(styleString, 'fontStyle', this._calcFontStyle(styleSource));
    styleString = mxUtils.setStyle(styleString, 'fontSize', parseInt(styleSource.fontSize, 10));

    if (isSticker(cell)) {
      cell.skipUndo = true;
    }

    model.setStyle(cell, styleString);
  }

  public get selectedActivityIds(): number[] {
    return this.registry
      .getGraph()
      .getSelectionCells()
      .filter((cell: MxCell) => isActivity(cell))
      .map(({id}: MxCell) => Number(id));
  }

  private _calcFontStyle(styleSource) {
    const x = styleSource.fontBold ? 1 : 0;
    const y = styleSource.fontItalic ? 2 : 0;
    const z = styleSource.fontUnderline ? 4 : 0;

    return x + y + z;
  }
}
